import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Search, X, Plus, Check, ChevronUp, ChevronDown } from 'lucide-react';
import useClickOutside from 'src/hooks/useClickOutside';

export function CombinedCertificationsInput({
  options = [],
  value = { certifications: [] },
  onChange,
  placeholder = 'Search certifications...',
  className = '',
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [issuingBody, setIssuingBody] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const customFormRef = useRef(null);
  const [formPosition, setFormPosition] = useState({ top: 0, left: 0 });

  const selectedCerts = value.certifications || [];

  // Build display list from selected cert objects
  const allSelectedCerts = React.useMemo(
    () =>
      selectedCerts.map((c) => ({
        value:
          c.ref_certification_id != null
            ? c.ref_certification_id.toString()
            : `custom_${c.name}`,
        label: c.name,
        issuingBody: c.issuingBody || null,
      })),
    [selectedCerts]
  );

  // Group options by category if available
  const groupedOptions = React.useMemo(() => {
    const hasCategories = options.some(
      (opt) => opt.category || opt.category_name
    );
    if (!hasCategories) {
      return options.length > 0 ? { 'All Certifications': options } : {};
    }

    const grouped = {};
    options.forEach((opt) => {
      const category = opt.category || opt.category_name || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(opt);
    });
    const sorted = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    const sortedGrouped = {};
    sorted.forEach((key) => {
      sortedGrouped[key] = grouped[key];
    });
    return sortedGrouped;
  }, [options]);

  // Filter options based on search
  const filteredGroupedOptions = React.useMemo(() => {
    if (!searchTerm.trim()) return groupedOptions;

    const filtered = {};
    Object.keys(groupedOptions).forEach((category) => {
      const filteredItems = groupedOptions[category].filter(
        (opt) =>
          opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (opt.abbreviation &&
            opt.abbreviation.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      if (filteredItems.length > 0) {
        filtered[category] = filteredItems;
      }
    });
    return filtered;
  }, [searchTerm, groupedOptions]);

  // Expand all categories by default
  useEffect(() => {
    const categories = Object.keys(groupedOptions);
    const initialExpanded = {};
    categories.forEach((cat) => {
      initialExpanded[cat] = true;
    });
    setExpandedCategories(initialExpanded);
  }, [groupedOptions]);

  // Inject scrollbar styles
  useEffect(() => {
    const styleId = 'certifications-scrollbar-styles';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .certifications-scrollbar::-webkit-scrollbar {
        width: 6px;
      }
      .certifications-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .certifications-scrollbar::-webkit-scrollbar-thumb {
        background: var(--ah-border-card);
        border-radius: 3px;
      }
      .certifications-scrollbar::-webkit-scrollbar-thumb:hover {
        background: var(--ah-border-card-light);
      }
      .certifications-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: var(--ah-border-card) transparent;
      }
      .certifications-chips-scrollbar::-webkit-scrollbar {
        width: 4px;
        height: 4px;
      }
      .certifications-chips-scrollbar::-webkit-scrollbar-track {
        background: transparent;
      }
      .certifications-chips-scrollbar::-webkit-scrollbar-thumb {
        background: var(--ah-border-card);
        border-radius: 2px;
      }
      .certifications-chips-scrollbar {
        scrollbar-width: thin;
        scrollbar-color: var(--ah-border-card) transparent;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  // Check if search term matches any existing option exactly
  const hasExactMatch = React.useMemo(() => {
    if (!searchTerm.trim()) return false;
    return options.some(
      (opt) =>
        opt.label.toLowerCase() === searchTerm.toLowerCase().trim() ||
        opt.value.toLowerCase() === searchTerm.toLowerCase().trim()
    );
  }, [searchTerm, options]);

  // Check if search term is already selected (by name)
  const isSearchTermSelected = React.useMemo(() => {
    if (!searchTerm.trim()) return false;
    const searchLower = searchTerm.toLowerCase().trim();
    return selectedCerts.some((c) => c.name.toLowerCase() === searchLower);
  }, [searchTerm, selectedCerts]);

  useClickOutside(
    customFormRef,
    () => {
      setShowCustomForm(false);
      setIssuingBody('');
    },
    showCustomForm
  );

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Check if a master-data option is already selected
  const isOptionSelected = (option) => {
    const refId = parseInt(option.value, 10);
    return selectedCerts.some(
      (c) => c.ref_certification_id === refId || c.name === option.label
    );
  };

  const handleToggleCertification = (option) => {
    if (isOptionSelected(option)) {
      const refId = parseInt(option.value, 10);
      onChange({
        ...value,
        certifications: selectedCerts.filter(
          (c) => c.ref_certification_id !== refId && c.name !== option.label
        ),
      });
    } else {
      onChange({
        ...value,
        certifications: [
          ...selectedCerts,
          {
            certification_id: null,
            ref_certification_id: parseInt(option.value, 10),
            name: option.label,
            issuingBody: null,
          },
        ],
      });
    }
  };

  const handleOpenCustomForm = () => {
    if (!searchTerm.trim() || isSearchTermSelected) return;

    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setFormPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }

    setShowCustomForm(true);
  };

  const handleCancelCustomForm = () => {
    setShowCustomForm(false);
    setIssuingBody('');
  };

  const handleSubmitCustomForm = () => {
    if (!searchTerm.trim()) return;

    onChange({
      ...value,
      certifications: [
        ...selectedCerts,
        {
          certification_id: null,
          ref_certification_id: null,
          name: searchTerm.trim(),
          issuingBody: issuingBody.trim() || null,
        },
      ],
    });

    setSearchTerm('');
    setIssuingBody('');
    setShowCustomForm(false);
  };

  const handleRemoveCertification = (cert) => {
    onChange({
      ...value,
      certifications: selectedCerts.filter((c) => c.name !== cert.label),
    });
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleInputKeyDown = (e) => {
    if (
      e.key === 'Enter' &&
      searchTerm.trim() &&
      !hasExactMatch &&
      !isSearchTermSelected &&
      !showCustomForm
    ) {
      e.preventDefault();
      handleOpenCustomForm();
    }
  };

  return (
    <div className={`relative w-full ${className}`} ref={containerRef}>
      {/* Selected Certifications Chips */}
      {allSelectedCerts.length > 0 && (
        <div className="max-h-[140px] overflow-y-auto overflow-x-hidden certifications-chips-scrollbar mb-3">
          <div className="flex flex-wrap gap-2">
            {allSelectedCerts.map((cert) => (
              <span
                key={cert.value}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ah-brand-primary text-white text-xs font-medium flex-shrink-0"
              >
                <span>{cert.label}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveCertification(cert)}
                  className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
                  aria-label={`Remove ${cert.label}`}
                >
                  <X size={12} className="text-white" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Search Input */}
      <div className="relative flex items-center w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2.5 text-sm mb-3 focus-within:ring-2 focus-within:ring-ah-brand-primary focus-within:border-ah-brand-primary">
        <Search className="h-4 w-4 text-[var(--text-faint)] mr-2 flex-shrink-0" />
        <input
          ref={searchInputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholder}
          className="flex-1 outline-none bg-transparent text-sm text-[var(--text)] placeholder:text-[var(--text-faint)]"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="ml-2 text-[var(--text-faint)] hover:text-[var(--text-muted)] transition-colors flex-shrink-0"
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Certifications List - Always Visible */}
      <div className="border border-[var(--border)] rounded-md bg-[var(--surface)] overflow-hidden">
        <div
          className="overflow-y-auto certifications-scrollbar"
          style={{ maxHeight: '25vh', minHeight: '200px' }}
        >
          {/* No match + Add custom button */}
          {searchTerm.trim() &&
            Object.keys(filteredGroupedOptions).length === 0 &&
            !hasExactMatch &&
            !isSearchTermSelected && (
              <div className="p-8 text-center">
                <Search
                  size={24}
                  className="mx-auto mb-2 opacity-50 text-[var(--text-faint)]"
                />
                <p className="text-sm text-[var(--text-faint)] !text-center mb-4">
                  No match for{' '}
                  <span className="font-semibold">
                    &quot;{searchTerm}&quot;
                  </span>
                </p>
                <button
                  type="button"
                  onClick={handleOpenCustomForm}
                  className="mx-auto flex items-center gap-2 px-4 py-2.5 rounded-md bg-ah-brand-primary text-white font-medium text-sm transition-colors hover:bg-ah-brand-alt-hover"
                >
                  <Plus size={16} />
                  Add{' '}
                  <span className="font-semibold">
                    &quot;{searchTerm}&quot;
                  </span>{' '}
                  as custom
                </button>
              </div>
            )}

          {/* Grouped Options */}
          {Object.keys(filteredGroupedOptions).length > 0 && (
            <div>
              {Object.entries(filteredGroupedOptions).map(
                ([category, categoryOptions]) => {
                  const isExpanded = expandedCategories[category] !== false;
                  return (
                    <div
                      key={category}
                      className="border-b border-[var(--border-light)] last:border-b-0"
                    >
                      <button
                        type="button"
                        onClick={() => toggleCategory(category)}
                        className="w-full px-4 py-2.5 bg-[var(--surface-muted)] hover:bg-[var(--border-light)] flex items-center justify-between text-left transition-colors"
                      >
                        <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide">
                          {category}
                        </span>
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-[var(--text-faint)]" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-[var(--text-faint)]" />
                        )}
                      </button>
                      {isExpanded && (
                        <div>
                          {categoryOptions.map((option) => {
                            const isSelected = isOptionSelected(option);
                            return (
                              <button
                                key={option.value}
                                type="button"
                                onClick={() =>
                                  handleToggleCertification(option)
                                }
                                className={`w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-[var(--primary-light)] transition-colors ${
                                  isSelected ? 'bg-[var(--primary-light)]' : ''
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                      isSelected
                                        ? 'bg-ah-brand-primary border-ah-brand-primary'
                                        : 'border-[var(--border)]'
                                    }`}
                                  >
                                    {isSelected && (
                                      <Check size={12} className="text-white" />
                                    )}
                                  </div>
                                  <span className="text-sm text-[var(--text)]">
                                    {option.label}
                                  </span>
                                </div>
                                {option.abbreviation && (
                                  <span className="text-xs text-[var(--text-faint)] bg-[var(--surface-muted)] px-2 py-0.5 rounded-full flex-shrink-0">
                                    {option.abbreviation}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>
          )}

          {/* Empty state */}
          {!searchTerm.trim() &&
            Object.keys(filteredGroupedOptions).length === 0 && (
              <div className="p-8 text-center text-[var(--text-faint)]">
                <p className="text-sm">No certifications available</p>
              </div>
            )}
        </div>
      </div>

      {/* Custom Certification Form */}
      {showCustomForm &&
        createPortal(
          <div
            ref={customFormRef}
            data-combined-cert-portal=""
            className="fixed rounded-md border border-[var(--border)] bg-[var(--surface)] text-[var(--text)] shadow-lg z-[9999]"
            style={{
              top: `${formPosition.top / 2}px`,
              left: `${formPosition.left}px`,
              width: `${containerRef.current?.offsetWidth || 400}px`,
            }}
          >
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5">
                  Certification name
                </label>
                <input
                  type="text"
                  value={searchTerm}
                  readOnly
                  className="w-full px-3 py-2 text-sm border border-[var(--border)] rounded-md bg-[var(--surface-muted)] text-[var(--text-muted)] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[var(--text-muted)] mb-1.5">
                  Issuing body{' '}
                  <span className="text-[var(--text-faint)] font-normal">
                    (optional)
                  </span>
                </label>
                <input
                  type="text"
                  value={issuingBody}
                  onChange={(e) => setIssuingBody(e.target.value)}
                  placeholder="Issuing body (optional)"
                  className="w-full px-3 py-2 text-sm border border-[var(--border)] rounded-md bg-[var(--surface)] text-[var(--text)] placeholder:text-[var(--text-faint)] focus:outline-none focus:ring-2 focus:ring-ah-brand-primary focus:border-transparent"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmitCustomForm();
                    }
                  }}
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 px-4 pb-4">
              <button
                type="button"
                onClick={handleCancelCustomForm}
                className="px-4 py-2 text-sm font-medium text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitCustomForm}
                className="px-4 py-2 text-sm font-medium bg-ah-brand-primary text-white rounded-md hover:bg-ah-brand-alt-hover transition-colors"
              >
                Add
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
